import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { hashPassword } from '../../utils/auth';
import { Search, Plus, X, Pencil, Trash2, PowerOff, Power, KeyRound } from 'lucide-react';
import { UserForm } from './UserForm';
import { useConfirm } from '../../contexts/ConfirmContext';
import { toast } from 'sonner';

interface User {
  id: string;
  username: string;
  email: string;
  phone?: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
  assignedReports?: string[];
  isActive?: boolean;
}

const ROLE_CONFIG: Record<User['role'], { label: string; className: string }> = {
  admin: {
    label: 'Administrateur',
    className: 'bg-[#00665C] text-white border border-[#004d45]',
  },
  pasteur: {
    label: 'Pasteur',
    className: 'bg-[#F2B636] text-white border border-[#e09a16]',
  },
  responsable: {
    label: 'Responsable',
    className: 'bg-[#00665C]/15 text-[#00665C] border border-[#00665C]/30',
  },
  user: {
    label: 'Utilisateur',
    className: 'bg-gray-100 text-gray-600 border border-gray-200',
  },
};

function RoleBadge({ role }: { role: User['role'] }) {
  const config = ROLE_CONFIG[role] ?? ROLE_CONFIG.user;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

function StatusBadge({ isActive }: { isActive?: boolean }) {
  const active = isActive !== false; // undefined = actif par défaut
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${
        active
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-red-50 text-red-600 border-red-200'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {active ? 'Actif' : 'Inactif'}
    </span>
  );
}

export default function UsersList() {
  const confirm = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterRole, setFilterRole] = useState<User['role'] | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchUsers = async () => {
    const q = query(collection(db, 'users'), orderBy('username'));
    const querySnapshot = await getDocs(q);
    const usersData = querySnapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    })) as User[];
    setUsers(usersData);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (userData: any) => {
    try {
      setError('');
      if (editingUser) {
        const userRef = doc(db, 'users', editingUser.id);
        await updateDoc(userRef, userData);
        toast.success('Utilisateur modifié avec succès');
      } else {
        const emailQuery = query(collection(db, 'users'), where('email', '==', userData.email));
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) {
          setError('Un utilisateur avec cet email existe déjà');
          return;
        }
        await addDoc(collection(db, 'users'), {
          ...userData,
          isActive: true,
          createdAt: new Date()
        });
        toast.success('Utilisateur créé avec succès');
      }
      setShowForm(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      console.error('Error managing user:', err);
      setError('Une erreur est survenue');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (userId: string) => {
    const ok = await confirm({
      title: 'Supprimer l\'utilisateur',
      description: 'Cette action est irréversible. L\'utilisateur perdra tout accès à la plateforme.',
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      variant: 'danger'
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      toast.success('Utilisateur supprimé');
      await fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (user: User) => {
    const newActive = user.isActive === false ? true : false;
    const action = newActive ? 'activer' : 'désactiver';
    const ok = await confirm({
      title: `${newActive ? 'Activer' : 'Désactiver'} le compte`,
      description: newActive
        ? `${user.username} pourra se reconnecter à la plateforme.`
        : `${user.username} ne pourra plus se connecter à la plateforme.`,
      confirmLabel: newActive ? 'Activer' : 'Désactiver',
      cancelLabel: 'Annuler',
      variant: newActive ? 'default' : 'warning',
    });
    if (!ok) return;

    setTogglingId(user.id);
    try {
      await updateDoc(doc(db, 'users', user.id), { isActive: newActive });
      toast.success(
        newActive
          ? `Compte de ${user.username} activé`
          : `Compte de ${user.username} désactivé`
      );
      await fetchUsers();
    } catch (err) {
      console.error(`Error toggling user ${action}:`, err);
      toast.error(`Erreur lors de la mise à jour du statut`);
    } finally {
      setTogglingId(null);
    }
  };


  const handleChangePassword = async () => {
    if (!passwordTarget) return;
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setChangingPassword(true);
    try {
      const hashedPassword = await hashPassword(newPassword);
      await updateDoc(doc(db, 'users', passwordTarget.id), { password: hashedPassword });
      toast.success('Mot de passe de ' + passwordTarget.username + ' mis à jour');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordTarget(null);
    } catch {
      toast.error('Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setError('');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone?.includes(searchTerm) ?? false);
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const active = user.isActive !== false;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && active) ||
      (filterStatus === 'inactive' && !active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Compteurs par rôle
  const counts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const activeCount = users.filter(u => u.isActive !== false).length;
  const inactiveCount = users.filter(u => u.isActive === false).length;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
        <button
          onClick={() => { setShowForm(!showForm); setEditingUser(null); }}
          className="inline-flex items-center px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          {showForm && !editingUser ? (
            <><X size={20} className="mr-2" />Masquer</>
          ) : (
            <><Plus size={20} className="mr-2" />Nouvel utilisateur</>
          )}
        </button>
      </div>

      {/* Compteurs par rôle */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(Object.entries(ROLE_CONFIG) as [User['role'], typeof ROLE_CONFIG[User['role']]][]).map(([role]) => (
          <button
            key={role}
            onClick={() => setFilterRole(filterRole === role ? 'all' : role)}
            className={`p-3 rounded-lg border-2 text-left transition-all ${
              filterRole === role ? 'border-[#00665C] shadow-sm' : 'border-transparent bg-white shadow-sm'
            }`}
          >
            <div className="text-2xl font-bold text-gray-800">{counts[role] ?? 0}</div>
            <RoleBadge role={role} />
          </button>
        ))}
      </div>

      {/* Filtres statut actif/inactif */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500 font-medium">Statut :</span>
        {(['all', 'active', 'inactive'] as const).map(status => {
          const labels = { all: `Tous (${users.length})`, active: `Actifs (${activeCount})`, inactive: `Inactifs (${inactiveCount})` };
          return (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                filterStatus === status
                  ? status === 'inactive'
                    ? 'bg-red-100 text-red-700 border-red-300'
                    : status === 'active'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                    : 'bg-[#00665C]/10 text-[#00665C] border-[#00665C]/30'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
            >
              {labels[status]}
            </button>
          );
        })}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg">{error}</div>
          )}
          <UserForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            initialData={editingUser}
          />
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Rechercher par nom, email ou téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-400 text-sm">
                  Aucun utilisateur trouvé
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => {
                const isActive = user.isActive !== false;
                return (
                  <tr
                    key={user.id}
                    className={`transition-colors ${
                      isActive ? 'hover:bg-gray-50' : 'bg-gray-50/60 opacity-70 hover:opacity-90'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{user.email}</td>
                    <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-gray-600">
                      {user.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge isActive={user.isActive} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-1.5 text-[#00665C] hover:text-[#004d45] hover:bg-[#00665C]/5 rounded transition-colors"
                          title="Modifier"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={togglingId === user.id}
                          className={`p-1.5 rounded transition-colors disabled:opacity-40 ${
                            isActive
                              ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                              : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50'
                          }`}
                          title={isActive ? 'Désactiver le compte' : 'Activer le compte'}
                        >
                          {isActive ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
              <button
                onClick={() => { setPasswordTarget(user); setNewPassword(''); setConfirmPassword(''); setShowPasswordModal(true); }}
                title="Changer le mot de passe"
                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
              >
                <KeyRound size={16} />
              </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''} affiché{filteredUsers.length !== 1 ? 's' : ''}
          {filterRole !== 'all' && ` · rôle : ${ROLE_CONFIG[filterRole].label}`}
          {filterStatus !== 'all' && ` · statut : ${filterStatus === 'active' ? 'actifs' : 'inactifs'}`}
        </div>
      </div>
    
{/* Modal changement de mot de passe */}
      {showPasswordModal && passwordTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-blue-50">
                <KeyRound className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Changer le mot de passe</h3>
                <p className="text-sm text-gray-500">{passwordTarget.username}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min. 6 caractères"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Répéter le mot de passe"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => { setShowPasswordModal(false); setPasswordTarget(null); }}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {changingPassword ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
  );
}